import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { apiClient } from '@/services/apiClient';
import { toast } from 'sonner';
import type { Editor } from '@tiptap/react';
import { useContent, useQualityReport, useSchemaPreview, useInvalidateSchemaPreview } from '@/hooks/useBlogCreationAPI';
import { useContentComments } from '@/hooks/useContentComments';
import { useDebounce } from '@/hooks/use-debounce';
import { setEditorMetadataDirty } from '@/webmcp/editorMetaDirty';
import { blogCreationService } from '@/services/blogCreationService';
import type {
  AuthoredFaq,
  AuthoredSchemaFields,
  AuthoredSchemaPatch,
  BreadcrumbParticle,
  HowToObject,
} from '@/services/blogCreationService';
import {
  deriveMetaTitle,
  metadataPatchFields,
  type MetadataValues,
} from './metadataPatch';
import {
  readPostgenEvidence,
  type MetadataFieldState,
} from './postgenEvidence';
import {
  editorAIService,
  type EditorImageOpportunity,
  type EditorInlineSuggestion,
  type EditorSnapshot,
} from '@/services/editorAIService';
import { useTenantStore } from '@/stores/useTenantStore';
import type { ContentDetail } from '@/components/BlogCreationCommandCenter/types';
import type {
  CommentsWithRepliesByBlock,
  ContentComment,
} from '@/types/contentFeedback';

import { EditorRail } from './EditorRail';
import { ToolsTab } from './ToolsTab';
import { StructuredDataDrawer } from './StructuredDataDrawer';
import { SocialMetadataCard } from './cards/SocialMetadataCard';
import { PublishingSettingsCard } from './cards/PublishingSettingsCard';
import { canAutosaveSlugChange, type SlugAvailability } from './slugAvailability';
import { CommentsTab } from './CommentsTab';
import { useStructureSuggestions } from './useStructureSuggestions';
import {
  DEMO_SCHEMAS,
} from './demoData';
import type {
  ContentQualityCardProps,
  QualityFinding,
} from './cards/ContentQualityCard';
import type { TopicCoverageItem } from './cards/TopicCoverageCard';
import type { TrustSignal } from './cards/TrustSignalsCard';
import type { QualityReportResponse } from '@/types/qualityReport';
import type { InternalLinkSuggestion } from './cards/InternalLinksCard';
import type { ExternalLinkItem } from './cards/ExternalLinksCard';
import type { SerpPreviewData } from './cards/SerpPreviewCard';
import type { ImageSuggestion } from './cards/ImageSuggestionsCard';
import type { EditorImageMeta } from './cards/ImageMetadataCard';
import type { SchemaType } from './cards/SchemaTypesCard';
import type {
  SchemaTypePresence,
  SchemaFocusType,
} from './cards/StructuredDataCard';
import type { WordPressCardProps, WordPressState } from './cards/WordPressCard';
import type { RailComment } from './CommentsList';

export interface UniversalEditorRailMountProps {
  contentId: string;
  /** Word/character count derived from the editor's live text. */
  wordCount: number;
  charCount: number;
  /** Heading count derived from the editor's TipTap doc. Optional. */
  headingCount?: number;
  /**
   * Inline-suggestions toggle. When provided, the rail is controlled and the
   * parent (EditorShell / overlays-builder) owns the on/off state so suggestion
   * underlines in the doc surface stay in sync with the rail toggle.
   */
  suggestionsOn?: boolean;
  setSuggestionsOn?: (next: boolean) => void;
  getEditorSnapshot?: () => EditorSnapshot;
  /**
   * Live TipTap editor instance — required for the Structure card to walk
   * heading nodes and for onAddSuggested to insert into the doc.
   */
  tiptapEditor?: Editor | null;
  /** Optional render slot for trailing content under the cards. */
  trailingTools?: ReactNode;
}

type RailContentDetail = ContentDetail & {
  featured_image_id?: number | null;
  schema_types_injected?: string[] | null;
  /** Post excerpt override (backend contract: string | null, override-only). */
  excerpt?: string | null;
  /** Generation-side JSON-LD enrichment blob (backend contract: object | null). */
  postgen_jsonld?: object | null;
  /**
   * Authored-schema typed extractor (Session A adds to ContentDetail — until
   * that lands we read defensively from generation_metadata.postgen_schema.fields
   * via readAuthoredSchema, which tolerates both shapes). The publisher reads
   * ONLY this packet for the Article.description + HowTo enrichment.
   */
  postgen_schema_fields?: AuthoredSchemaFields | null;
};

// ---------------------------------------------------------------------------
// Authored-schema drawer field/run types (the authored-schema props
// themselves live on StructuredDataDrawerProps).
// ---------------------------------------------------------------------------

type AuthoredSchemaFieldName = 'article_description' | 'howto';
type SchemaRunStatus = 'queued' | 'running' | 'applied' | 'failed' | null;

/** Read-only, document-derived state for the summary/image/structured-data cards. */
interface EditorDocSummary {
  /** First body paragraph — the automatic excerpt fallback. */
  derivedExcerpt: string;
  /** Key-takeaways callout items, in order. */
  takeaways: string[];
  /** Every image in the document, in order, with editable alt/caption. */
  images: EditorImageMeta[];
  /** Whether the body contains FAQ blocks (drives the FAQPage schema row). */
  hasFaqBlocks: boolean;
}

const EMPTY_DOC_SUMMARY: EditorDocSummary = {
  derivedExcerpt: '',
  takeaways: [],
  images: [],
  hasFaqBlocks: false,
};

type RailImageSuggestion = ImageSuggestion & {
  prompt?: string;
  imageType?: string;
  rationale?: string;
};

type ExistingContentImage = {
  src: string;
  alt?: string | null;
};

/**
 * Mounts EditorRail into the editor's right rail and wires each card to the
 * project's existing data sources where available. Cards without a backing
 * service use the demo data from `demoData.ts` and a `// TODO(rail)` comment
 * marks the future wire-point.
 *
 * Wired:
 *   ContentQualityCard    → useQualityReport (via blogCreationService)
 *   ContentStatsCard      → live wordCount/charCount/headingCount props
 *   SerpPreviewCard       → useContent (meta_title, meta_description, url_slug)
 *   WordPressCard         → useContent (wordpress_post_id, status)
 *   SocialRepurposeCard   → useSocialMediaRepurpose mutation
 *   CommentsTab           → useContentComments (createComment, resolveComment)
 *   TopicCoverageCard     → useContent.target_keywords (falls back to demo)
 *   InternalLinksCard     → useContent.internal_links
 *   StructureChecklistCard → useStructureSuggestions (live heading walker + editor-ai)
 *
 * Demo + TODO:
 *   SuggestionsCard       — TODO(rail): wire to suggestion underline service
 *   TrustSignalsCard      — TODO(rail): wire to reality/authority/temporal validators
 *   HeroImageCard         — TODO(rail): wire to media-fetch service (BL-09)
 *   ImageSuggestionsCard  — TODO(rail): wire to media analyze endpoint
 *   SchemaTypesCard       — TODO(rail): wire to schemaService
 *   Ask AI                — centralized chat adapter with editor context
 */
export function UniversalEditorRailMount({
  contentId,
  wordCount,
  headingCount = 0,
  suggestionsOn: suggestionsOnProp,
  setSuggestionsOn: setSuggestionsOnProp,
  getEditorSnapshot,
  tiptapEditor,
  trailingTools,
}: UniversalEditorRailMountProps) {
  const tenantId = useTenantStore((s) => s.currentTenant) ?? '';

  // --- Suggestions toggle: controlled if parent owns it, else local. ---
  const [suggestionsOnLocal, setSuggestionsOnLocal] = useState(true);
  // QA/noindex approval (#9): shown when the post was published with the QA
  // noindex meta; approving clears it in WP + the row.
  const [indexingAllowed, setIndexingAllowed] = useState(false);
  const [indexingSubmitting, setIndexingSubmitting] = useState(false);
  const handleAllowIndexing = useCallback(async () => {
    if (!contentId) return;
    setIndexingSubmitting(true);
    try {
      await apiClient.post(
        `/blog-creation/content/${contentId}/allow-indexing`,
        null,
        { params: { tenant_id: tenantId } },
      );
      setIndexingAllowed(true);
    } catch (err) {
      console.error('[ALLOW_INDEXING] failed', err);
    } finally {
      setIndexingSubmitting(false);
    }
  }, [contentId, tenantId]);
  const suggestionsOn = suggestionsOnProp ?? suggestionsOnLocal;
  const setSuggestionsOn = setSuggestionsOnProp ?? setSuggestionsOnLocal;
  const [railSuggestions, setRailSuggestions] = useState<EditorInlineSuggestion[]>([]);
  const [suggestionsGenerating, setSuggestionsGenerating] = useState(false);
  const [internalLinkSuggestions, setInternalLinkSuggestions] =
    useState<InternalLinkSuggestion[]>([]);
  const [externalLinkItems, setExternalLinkItems] = useState<ExternalLinkItem[]>([]);
  // Start all-unchecked (never the DEMO_SCHEMAS defaults — see seedSchemaTypes):
  // the real state seeds from schema_types_injected once content loads.
  const [schemaTypes, setSchemaTypes] = useState<SchemaType[]>(() =>
    seedSchemaTypes(null),
  );
  const [imageSuggestions, setImageSuggestions] = useState<RailImageSuggestion[]>([]);
  const [imageAnalyzed, setImageAnalyzed] = useState(false);
  const [imageAnalyzing, setImageAnalyzing] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [heroImageAlt, setHeroImageAlt] = useState<string | null>(null);
  const [heroGenerating, setHeroGenerating] = useState(false);
  const [wpAuthors, setWpAuthors] = useState<Array<{ id: number; name: string }>>([]);

  // --- Data hooks ---
  const { data: content, refetch: refetchContent } = useContent(contentId);
  const railContent = content as RailContentDetail | undefined;
  const { data: qualityReport } = useQualityReport(contentId);
  const {
    commentsByBlock,
    createComment,
    resolveComment,
  } = useContentComments(contentId, tenantId);

  // --- E0: read the post-generation metadata evidence that already rides to
  //     the client in content.generation_metadata. Zero backend; pure accessor
  //     (see postgenEvidence.ts). Feeds the field-state chips, the needs-review
  //     counter on the metadata control, the per-field revert list, and the
  //     whole-run Undo affordance. ---
  const metadataSummary = readPostgenEvidence(content);

  // --- Structured-data drawer (Family E). The drawer is non-modal and lazy:
  //     the schema-preview hook is only enabled when the drawer is open. Once
  //     cached, the rail card reads the ship summary + the per-type "no value
  //     yet" cues from the same cache entry even when the drawer is closed. ---
  const [schemaDrawerOpen, setSchemaDrawerOpen] = useState(false);
  // Card row taps carry a schema-type focus; the mount stores it here,
  // opens the drawer, and passes it down as the scoped-open input. Cleared
  // when the drawer closes (per-open-session, never persisted).
  const [schemaDrawerFocus, setSchemaDrawerFocus] = useState<SchemaFocusType | null>(null);
  const { data: schemaPreview } = useSchemaPreview(contentId, schemaDrawerOpen);
  const invalidateSchemaPreview = useInvalidateSchemaPreview();

  // --- Authored-schema section (LOCKED contract — Session C wires, Session B
  //     renders). The publisher reads generation_metadata.postgen_schema.fields
  //     for Article.description + HowTo enrichment; edits land there via
  //     blogCreationService.updateAuthoredSchema with a sibling `edited=true`
  //     stamp (Session A) so a later Family-E re-run won't clobber them.
  //
  //     Edit + persist path mirrors the metadataOverride → debounced patchContent
  //     pattern: a local override holds in-flight edits, an 800ms-debounced
  //     effect calls updateAuthoredSchema, and on success we invalidate
  //     ['schema-preview', contentId] (today nothing does) + refetchContent so
  //     the composed graph refreshes.
  //
  //     The run-status flip → 'applied' (Family-E completes) auto-fills the
  //     fields: we invalidate the schema-preview cache on that transition so
  //     the drawer re-fetches with the freshly-authored values.
  const schemaRunStatus: SchemaRunStatus = metadataSummary.status;
  const baseAuthoredSchema = useMemo<AuthoredSchemaFields | null>(
    () => readAuthoredSchema(railContent),
    [railContent],
  );
  const [authoredSchemaOverride, setAuthoredSchemaOverride] =
    useState<AuthoredSchemaFields | null>(null);
  const authoredSchema = authoredSchemaOverride ?? baseAuthoredSchema;

  // T4 rail-card presence rows (Carl/Camille design ruling): 5 fixed rows,
  // one per schema type, right side is VALUE PRESENCE — never an aggregate
  // count. Article/HowTo read the authored-schema packet (same source the
  // drawer edits); FAQPage reads the composed jsonld from the SAME lazy
  // schema-preview cache used above (no forced fetch — stays gated on drawer
  // open); Product has no backing source yet.
  const schemaTypePresence = useMemo<SchemaTypePresence[]>(() => {
    const description = authoredSchema?.article_description?.trim() ?? '';
    const articleRow: SchemaTypePresence = description
      ? {
          type: 'Article',
          locked: true,
          state: 'value',
          detail: quotedSnippet(description),
        }
      : {
          type: 'Article',
          locked: true,
          state: 'empty',
          detail: 'no value yet',
        };

    // Breadcrumb trail — real crumbs from the persisted authored packet
    // (available immediately on ContentDetail, no lazy preview fetch needed).
    // Falls back to the live schema-preview BreadcrumbList node, then to
    // "no value yet" only when truly absent from both sources.
    const breadcrumbTrail =
      breadcrumbTrailFromParticles(authoredSchema?.breadcrumbs) ??
      breadcrumbTrailFromPreview(schemaPreview?.jsonld);
    const breadcrumbRow: SchemaTypePresence = breadcrumbTrail
      ? {
          type: 'BreadcrumbList',
          locked: true,
          state: 'value',
          detail: breadcrumbTrail,
        }
      : {
          type: 'BreadcrumbList',
          locked: true,
          state: 'empty',
          detail: 'no value yet',
        };

    const howToStepCount = authoredSchema?.is_howto
      ? authoredSchema.howto?.steps?.length ?? 0
      : 0;
    const howToRow: SchemaTypePresence =
      howToStepCount > 0
        ? {
            type: 'HowTo',
            state: 'value',
            detail: `${howToStepCount} ${howToStepCount === 1 ? 'step' : 'steps'}`,
          }
        : { type: 'HowTo', state: 'empty', detail: 'no value yet' };

    // FAQ count — prefer the persisted authored packet (immediate, no lazy
    // preview fetch); fall back to the lazy schema-preview count. The ≥2-pair
    // publish gate is applied via faqPresenceState either way.
    let faqRow: SchemaTypePresence;
    if (authoredSchema?.faq) {
      faqRow = {
        type: 'FAQPage',
        ...faqPresenceState(authoredSchema.faq.pairs.length),
      };
    } else if (!schemaPreview) {
      faqRow = { type: 'FAQPage', state: 'unavailable', detail: '—' };
    } else {
      faqRow = {
        type: 'FAQPage',
        ...faqPresenceState(countFaqQuestions(schemaPreview.jsonld)),
      };
    }

    const productRow: SchemaTypePresence = {
      type: 'Product',
      state: 'unavailable',
      detail: 'not available yet',
    };

    return [articleRow, breadcrumbRow, howToRow, faqRow, productRow];
  }, [authoredSchema, schemaPreview]);

  // Debounced persist of authored-schema edits (article_description / howto).
  // The effect diffs the override against the base and PATCHes only when the
  // shape differs. Tenant guard mirrors the metadata save effect.
  const baseForDiffRef = useRef<AuthoredSchemaFields | null>(baseAuthoredSchema);
  baseForDiffRef.current = baseAuthoredSchema;
  useEffect(() => {
    if (!authoredSchemaOverride) return;
    if (!contentId) return;
    if (!tenantId) {
      console.warn('[rail] skipped authored-schema save because no tenant is selected');
      return;
    }
    const base = baseForDiffRef.current;
    const patch: AuthoredSchemaPatch = {};
    if (authoredSchemaOverride.article_description !== (base?.article_description ?? '')) {
      patch.article_description = authoredSchemaOverride.article_description;
    }
    if (!howToEqual(authoredSchemaOverride.howto, base?.howto ?? null)) {
      patch.howto = authoredSchemaOverride.howto;
    }
    if (patch.article_description === undefined && patch.howto === undefined) return;

    const timeoutId = window.setTimeout(() => {
      blogCreationService
        .updateAuthoredSchema(contentId, patch, tenantId)
        .then(() => {
          // The composed JSON-LD graph + per-field provenance depend on these
          // values; invalidate so the drawer re-fetches the REAL publisher
          // composition. Also refetchContent so the typed extractor
          // (postgen_schema_fields) + the edited/edited_at stamps land.
          invalidateSchemaPreview(contentId);
          void refetchContent();
        })
        .catch((err) => {
          console.error('[rail] save authored schema failed', err);
          toast.error('Could not save authored schema');
        });
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [
    authoredSchemaOverride,
    contentId,
    invalidateSchemaPreview,
    refetchContent,
    tenantId,
  ]);

  // When the Family-E run flips to 'applied' the freshly-authored fields land
  // on the row. Invalidate the schema-preview cache + clear any stale local
  // override so the drawer auto-fills with the new authored values.
  const previousSchemaRunStatusRef = useRef<SchemaRunStatus>(schemaRunStatus);
  useEffect(() => {
    const prev = previousSchemaRunStatusRef.current;
    previousSchemaRunStatusRef.current = schemaRunStatus;
    if (prev && prev !== 'applied' && schemaRunStatus === 'applied' && contentId) {
      invalidateSchemaPreview(contentId);
      // Drop the override so the base (re-fetched with the run's output) shows.
      setAuthoredSchemaOverride(null);
      void refetchContent();
    }
  }, [schemaRunStatus, contentId, invalidateSchemaPreview, refetchContent]);

  const handleEditAuthoredField = useCallback(
    (field: AuthoredSchemaFieldName, value: string | HowToObject) => {
      setAuthoredSchemaOverride((current) => {
        const base = current ?? baseForDiffRef.current;
        if (!base) return current;
        if (field === 'article_description' && typeof value === 'string') {
          return { ...base, article_description: value };
        }
        if (field === 'howto' && (value === null || isHowToObject(value))) {
          return { ...base, howto: value as HowToObject | null };
        }
        return current;
      });
    },
    [],
  );

  const handleForceHowTo = useCallback((): Promise<HowToObject | null> => {
    if (!contentId) return Promise.resolve(null);
    return blogCreationService.suggestHowTo(contentId, tenantId);
  }, [contentId, tenantId]);

  // --- On-demand postgen metadata run (Families A+B), triggered from the
  //     structured-data drawer's generate action and MetadataFixRow — the
  //     bulk rail card that used to own this state was removed (per-field
  //     AiSuggestButton covers that job per-field instead). ---
  const [metaRunRequesting, setMetaRunRequesting] = useState(false);
  const metadataRunStatus =
    (content as RailContentDetail | undefined)?.metadata_generation_status ?? null;
  const metadataGenerating =
    metaRunRequesting || metadataRunStatus === 'queued' || metadataRunStatus === 'running';
  const handleGenerateMetadata = useCallback(async () => {
    if (!contentId) return;
    // Snapshot the current metadata values BEFORE the run fires so the user
    // can one-click Undo the whole run later. Zero backend: the snapshot lives
    // only in this ref and is restored through the existing override→patch path.
    // (metadataRef.current is updated each render — see the metadata derivation
    // further down — so we don't need `metadata` in this callback's deps and
    // avoid a TDZ on the first render.)
    undoSnapshotRef.current = metadataRef.current;
    setUndoAvailable(false);
    setMetaRunRequesting(true);
    try {
      const result = await blogCreationService.regenerateMetadata(contentId, tenantId);
      if (result.alreadyRunning) {
        toast.info('A metadata run is already in progress');
      } else {
        toast.success('Metadata generation started');
      }
    } catch {
      toast.error('Could not start the metadata run');
    } finally {
      setMetaRunRequesting(false);
      refetchContent();
    }
  }, [contentId, tenantId, refetchContent]);

  // --- Metadata is one draft shared by the Chrome Meta card and compact SERP
  //     preview. This avoids the two editor surfaces overwriting one another. ---
  const [metadataOverride, setMetadataOverride] = useState<MetadataValues | null>(null);

  // --- Whole-run Undo (zero backend). Right before a regenerate fires, we
  //     snapshot the current MetadataValues into a ref. When the run status
  //     flips to 'applied', we surface a small "Undo this run" affordance that
  //     restores the snapshot through the existing metadataOverride→debounced
  //     patchContent path. The affordance auto-expires on the next user edit
  //     (any updateMetadata call clears it) so the user can't restore a stale
  //     snapshot over newer edits. ---
  const undoSnapshotRef = useRef<MetadataValues | null>(null);
  const [undoAvailable, setUndoAvailable] = useState(false);
  const previousMetaStatusRef = useRef<string | null>(null);

  const contentMetadata = useMemo<MetadataValues>(
    () => ({
      metaTitle: deriveMetaTitle(content?.meta_title),
      metaDescription: content?.meta_description || '',
      excerpt: (content as RailContentDetail | undefined)?.excerpt || '',
      slug: content?.url_slug || '',
      ogTitle: content?.og_title || '',
      ogDescription: content?.og_description || '',
      ogImage: content?.og_image_url || '',
      twitterTitle: content?.twitter_title || '',
      twitterDescription: content?.twitter_description || '',
      canonicalUrl: content?.canonical_url || '',
      robotsOverride: content?.robots_override || '',
      wordpressAuthorId: content?.wordpress_author_id ?? null,
      schemaTypes: content?.schema_types_injected ?? schemaLabels(schemaTypes),
      keyword: content?.keyword || '',
    }),
    [content, schemaTypes],
  );
  const metadata = metadataOverride ?? contentMetadata;
  // --- Slug-availability (live-typing, soft/advisory — see slugAvailability.ts).
  //     Debounced 250ms so we don't fire a request per keystroke; the status
  //     flips to 'checking' immediately (see the effect below) so the inline
  //     status line and canAutosaveSlugChange never read a stale 'idle'/
  //     'available' while a newer candidate is pending. ---
  const debouncedSlug = useDebounce(metadata.slug, 250);
  const [slugAvailability, setSlugAvailability] = useState<SlugAvailability>({
    status: 'idle',
  });
  const slugRequestControllerRef = useRef<AbortController | null>(null);
  // Latest metadata snapshot accessible from late-declared closures (e.g. the
  // generate handler defined further up). Updated each render; not in any deps.
  const metadataRef = useRef<MetadataValues>(metadata);
  metadataRef.current = metadata;
  // Surface the Undo affordance when the async run status flips to 'applied'
  // (and we actually have a pre-run snapshot to restore).
  useEffect(() => {
    const prev = previousMetaStatusRef.current;
    const next = metadataSummary.status;
    if (prev !== 'applied' && next === 'applied' && undoSnapshotRef.current) {
      setUndoAvailable(true);
    }
    previousMetaStatusRef.current = next;
  }, [metadataSummary.status]);
  const serp = useMemo<SerpPreviewData>(
    () =>
      ({
        title: metadata.metaTitle,
        description: metadata.metaDescription,
        url: metadata.slug
          ? `${tenantId} › ${metadata.slug}`
          : tenantId
            ? `${tenantId} › blog`
            : 'preview › untitled',
      }),
    [metadata.metaDescription, metadata.metaTitle, metadata.slug, tenantId],
  );

  const existingHeroImage = useMemo<ExistingContentImage | null>(
    () => extractFirstContentImage(railContent?.content_json),
    [railContent?.content_json],
  );

  // --- Document-derived state for the Summary / Images / Structured-data cards.
  //     Prefer the LIVE editor (so inline alt/caption edits and takeaways edits
  //     reflect immediately) and re-derive on every doc update — mirrors
  //     useStructureSuggestions. Falls back to the persisted content_json before
  //     the editor mounts. ---
  const heroSrc = heroImageUrl ?? existingHeroImage?.src ?? null;
  const [docSummary, setDocSummary] = useState<EditorDocSummary>(EMPTY_DOC_SUMMARY);
  useEffect(() => {
    const recompute = () => {
      setDocSummary(
        tiptapEditor
          ? summarizeEditorDoc(tiptapEditor, heroSrc)
          : summarizeContentJson(railContent?.content_json, heroSrc),
      );
    };
    recompute();
    if (!tiptapEditor) return;
    tiptapEditor.on('update', recompute);
    return () => {
      tiptapEditor.off('update', recompute);
    };
  }, [tiptapEditor, railContent?.content_json, heroSrc]);

  // The image the article currently treats as its hero (explicit "Set as hero"
  // designation wins inside the doc summary derivation).
  const railHeroImage = useMemo(
    () => docSummary.images.find((img) => img.isHero) ?? null,
    [docSummary.images],
  );

  useEffect(() => {
    setMetadataOverride(null);
    setImageAnalyzed(false);
    setImageSuggestions([]);
    setHeroImageUrl(null);
    setHeroImageAlt(null);
    // Reset the per-run Undo state when navigating between articles.
    undoSnapshotRef.current = null;
    setUndoAvailable(false);
    previousMetaStatusRef.current = null;
    // Reset the authored-schema override + run-status transition ref so the
    // new article's base packet shows and the next run→applied flip re-triggers
    // the schema-preview invalidation.
    setAuthoredSchemaOverride(null);
    previousSchemaRunStatusRef.current = null;
    // Reset slug-availability state when navigating between articles — a
    // stale 'conflict'/'checking' from the PREVIOUS article must never leak
    // onto the next one's slug field.
    slugRequestControllerRef.current?.abort();
    slugRequestControllerRef.current = null;
    setSlugAvailability({ status: 'idle' });
  }, [contentId]);

  // Flip the status to 'checking' immediately on every slug edit (before the
  // debounce below fires the actual request). This does two things: shows
  // "Checking…" right away instead of a stale prior result, and — since
  // canAutosaveSlugChange keys off slugAvailability.slug === candidate —
  // prevents an old 'available'/'conflict' result for a DIFFERENT candidate
  // from being misread as authoritative for this one. Also aborts whatever
  // request was in flight for the previous candidate.
  useEffect(() => {
    const slug = metadata.slug.trim();
    slugRequestControllerRef.current?.abort();
    slugRequestControllerRef.current = null;

    if (!slug || !tenantId || !contentId) {
      setSlugAvailability({ status: 'idle' });
      return;
    }

    setSlugAvailability({ status: 'checking', slug });
  }, [contentId, metadata.slug, tenantId]);

  // Run the actual tenant-scoped availability check after a short pause in
  // typing (GET /content/{id}/slug-availability — advisory only, see
  // slugAvailability.ts). Soft guardrail: this NEVER blocks the metadata
  // autosave below except for the brief in-flight window on this exact
  // candidate (canAutosaveSlugChange).
  useEffect(() => {
    const slug = debouncedSlug.trim();
    if (!slug || !tenantId || !contentId) {
      return;
    }

    // The user changed the field again while this debounce timer was
    // pending — the effect that will fire for the newer value supersedes
    // this one, so bail without issuing a now-stale request.
    if (slug !== metadataRef.current.slug.trim()) return;

    const controller = new AbortController();
    slugRequestControllerRef.current = controller;

    blogCreationService
      .checkSlugAvailability(contentId, slug, tenantId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted || slug !== metadataRef.current.slug.trim()) return;
        if (!result.normalized_slug || result.reason === 'invalid') {
          setSlugAvailability({ status: 'invalid', slug });
          return;
        }
        setSlugAvailability(
          result.available
            ? { status: 'available', slug }
            : { status: 'conflict', slug, conflict: result.conflict },
        );
      })
      .catch(() => {
        if (!controller.signal.aborted && slug === metadataRef.current.slug.trim()) {
          setSlugAvailability({ status: 'error', slug });
        }
      });

    return () => {
      controller.abort();
      if (slugRequestControllerRef.current === controller) {
        slugRequestControllerRef.current = null;
      }
    };
  }, [contentId, debouncedSlug, tenantId]);

  useEffect(() => {
    if (!metadataOverride) {
      setEditorMetadataDirty(false);
      return;
    }
    if (!tenantId) {
      console.warn('[rail] skipped metadata save because no tenant is selected');
      return;
    }

    const current = contentMetadata;
    if (JSON.stringify(metadataOverride) === JSON.stringify(current)) {
      setEditorMetadataDirty(false);
      return;
    }

    // There is a real unsaved metadata edit. Flag it for the agent-focus veto
    // NOW, before the slug guard and the 800ms debounce, so a concurrent agent
    // metadata write is refused for the whole window the buffer is unsaved. The
    // flag clears when THIS save resolves, not when `contentMetadata` refreshes,
    // because the content query is not invalidated on save.
    setEditorMetadataDirty(true);

    // Soft guardrail (see slugAvailability.ts): a conflict/error/invalid
    // result never blocks this save — only an availability check actively
    // in flight for this EXACT slug candidate does, to avoid racing the
    // GET against this PATCH.
    if (
      !canAutosaveSlugChange(
        metadataOverride.slug,
        current.slug,
        slugAvailability.status,
        slugAvailability.slug,
      )
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      blogCreationService
        .patchContent(contentId, metadataPatchFields(metadataOverride), tenantId)
        .then(() => setEditorMetadataDirty(false))
        .catch((err) => {
          console.error('[rail] save metadata failed', err);
          toast.error('Could not save SEO metadata');
        });
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [
    contentMetadata,
    contentId,
    metadataOverride,
    slugAvailability.slug,
    slugAvailability.status,
    tenantId,
  ]);

  // The rail owns the metadata-dirty signal only while it is mounted; clear it
  // on teardown so a route away from the editor cannot leave the veto stuck on.
  useEffect(() => () => setEditorMetadataDirty(false), []);

  // --- Quality card props from useQualityReport. ---
  // null = no real quality report yet; the rail renders an honest "not
  // analyzed" state. NEVER fall back to demo data here — fabricated scores on
  // real content misled QA into reporting a "quality score" on empty articles.
  const quality = useMemo<ContentQualityCardProps | null>(
    () => qualityToCardProps(qualityReport, content),
    [qualityReport, content],
  );

  // --- Trust signals from the Reality / Authority / Temporal validators.
  //     Data comes from the quality-report the mount already fetches; only
  //     sections that actually ran are shown (no demo fallback). ---
  const trustSignals = useMemo<TrustSignal[]>(
    () => trustToSignals(qualityReport),
    [qualityReport],
  );

  // --- TopicCoverage from target_keywords. ---
  const coverage = useMemo<{ items: TopicCoverageItem[] }>(() => {
    if (!content?.target_keywords?.length) return { items: [] };
    return {
      items: content.target_keywords.map((k, i) => ({
        keyword: k.keyword,
        used: countOccurrences(content.content_html ?? '', k.keyword),
        target: 3,
        sv: k.search_volume ?? undefined,
        kd: k.keyword_difficulty ?? undefined,
        cpc: k.cpc ?? undefined,
        primary: k.is_primary ?? i === 0,
      })),
    };
  }, [content]);

  // --- Internal/External links derived from the REAL document (content_json
  //     link marks). The legacy content.internal_links/external_links fields
  //     were never populated by generation, so the cards always showed 0;
  //     they remain only as a fallback for rows that do carry them. ---
  useEffect(() => {
    const fromDoc = extractLinksFromDoc(content?.content_json, tenantId);
    if (fromDoc.internal.length || fromDoc.external.length) {
      setInternalLinkSuggestions(fromDoc.internal);
      setExternalLinkItems(fromDoc.external);
      return;
    }
    setInternalLinkSuggestions(
      (content?.internal_links ?? []).map((l, i) => ({
        id: `il-${i}`,
        anchor: l.anchor,
        target: l.url,
        url: l.url,
        section: l.section != null ? `Section ${l.section}` : undefined,
      })),
    );
    setExternalLinkItems(
      (content?.external_links ?? []).map((l, i) => ({
        id: `el-${i}`,
        anchor: l.anchor,
        target: l.url,
        url: l.url,
        section: l.section != null ? `Section ${l.section}` : undefined,
      })),
    );
  }, [content?.content_json, content?.internal_links, content?.external_links, contentId, tenantId]);

  useEffect(() => {
    setSchemaTypes(seedSchemaTypes(railContent?.schema_types_injected));
  }, [contentId, railContent?.schema_types_injected]);

  useEffect(() => {
    if (!tenantId) {
      setWpAuthors([]);
      return;
    }
    let cancelled = false;
    blogCreationService.getWpAuthors(tenantId).then((authors) => {
      if (!cancelled) setWpAuthors(authors);
    }).catch(() => {
      if (!cancelled) setWpAuthors([]);
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  // --- WordPress card from useContent. ---
  const wordPress = useMemo<WordPressCardProps>(() => {
    const postId = content?.wordpress_post_id ?? null;
    const status = (content?.status ?? '') as string;
    let state: WordPressState = 'unpublished';
    if (postId && status === 'published') state = 'published';
    else if (postId) state = 'draft';
    else if (status === 'failed') state = 'error';
    return {
      state,
      postId,
      url: postId
        ? `https://${tenantId}/?p=${postId}`
        : null,
      onView: postId ? () => window.open(`https://${tenantId}/?p=${postId}`, '_blank') : undefined,
      onEditInWp: postId
        ? () =>
            window.open(
              `https://${tenantId}/wp-admin/post.php?post=${postId}&action=edit`,
              '_blank',
            )
        : undefined,
    };
  }, [content, tenantId]);

  // --- Structure card: live TipTap headings + AI-suggested missing H2s. ---
  const focusKeyword =
    content?.target_keywords?.[0]?.keyword ?? content?.keyword ?? '';
  const structureItems = useStructureSuggestions(
    contentId,
    tiptapEditor ?? null,
    focusKeyword,
  );

  // --- Comments adapter: flatten useContentComments grouping. ---
  const railComments = useMemo<RailComment[]>(
    () => flattenComments(commentsByBlock),
    [commentsByBlock],
  );

  const handleReply = useCallback(
    async (parentId: string, body: string) => {
      try {
        await createComment({
          comment_text: body,
          parent_comment_id: parentId,
          comment_type: 'general',
        });
      } catch (err) {
        console.error('[rail] reply failed', err);
        toast.error('Could not post reply');
      }
    },
    [createComment],
  );

  const handleResolve = useCallback(
    async (commentId: string) => {
      try {
        await resolveComment(commentId);
      } catch (err) {
        console.error('[rail] resolve failed', err);
        toast.error('Could not resolve comment');
      }
    },
    [resolveComment],
  );

  const handleGenerateSuggestions = useCallback(async () => {
    setSuggestionsGenerating(true);
    try {
      const response = await editorAIService.generateInlineSuggestions(contentId, getEditorSnapshot?.());
      setRailSuggestions(response.result.suggestions ?? []);
      toast.success(response.cached ? 'Loaded cached suggestions' : 'Generated inline suggestions');
    } catch (err) {
      console.error('[rail] generate suggestions failed', err);
      toast.error('Could not generate suggestions');
    } finally {
      setSuggestionsGenerating(false);
    }
  }, [contentId, getEditorSnapshot]);

  const handleApplySuggestion = useCallback((id: string) => {
    const suggestion = railSuggestions.find((item) => item.id === id);
    if (!suggestion) return;

    if (!tiptapEditor) {
      toast.error('Open the editor to apply suggestions');
      return;
    }

    const replaced = replaceFirstExactText(
      tiptapEditor,
      suggestion.text,
      suggestion.fix,
    );
    if (!replaced) {
      toast.error('Could not find that exact suggestion text in the document');
      return;
    }

    setRailSuggestions((current) => current.filter((item) => item.id !== id));
  }, [railSuggestions, tiptapEditor]);

  const handleDismissSuggestion = useCallback((id: string) => {
    setRailSuggestions((current) => current.filter((suggestion) => suggestion.id !== id));
  }, []);

  const updateMetadata = useCallback((update: Partial<MetadataValues>) => {
    // Any user edit auto-expires the whole-run Undo affordance so a stale
    // snapshot can never overwrite a newer edit.
    setUndoAvailable(false);
    setMetadataOverride((current) => ({
      ...(current ?? contentMetadata),
      ...update,
    }));
  }, [contentMetadata]);

  // --- Per-field revert-to-baseline (async evidence path only). The postgen
  //     evidence carries a pre-run baseline for some fields; we surface a tiny
  //     revert affordance per field that PATCHes just that one field back to
  //     its baseline through the existing metadataOverride→patchContent path.
  //     Minimal — the whole-run Undo above is the priority. ---
  const revertableFields = useMemo<MetadataFieldState[]>(
    () =>
      Object.values(metadataSummary.fieldStates).filter(
        (fs): fs is MetadataFieldState =>
          !!fs && typeof fs.baseline === 'string' && fs.baseline !== null,
      ),
    [metadataSummary.fieldStates],
  );

  const handleRevertField = useCallback(
    (field: string, baseline: string) => {
      const key = POSTGEN_FIELD_TO_METADATA_KEY[field];
      if (!key) return;
      if (key === 'robotsOverride') {
        const next = baseline === 'index' || baseline === 'noindex' ? baseline : '';
        updateMetadata({ robotsOverride: next });
        return;
      }
      updateMetadata({ [key]: baseline } as Partial<MetadataValues>);
    },
    [updateMetadata],
  );

  const handleUndoRun = useCallback(() => {
    const snapshot = undoSnapshotRef.current;
    if (!snapshot) return;
    setMetadataOverride(snapshot);
    undoSnapshotRef.current = null;
    setUndoAvailable(false);
    toast.info('Reverted metadata to the pre-run values');
  }, []);

  const handleSerpChange = useCallback((next: SerpPreviewData) => {
    updateMetadata({
      metaTitle: next.title,
      metaDescription: next.description,
    });
  }, [updateMetadata]);

  const handleRemoveInternalLink = useCallback(
    (link: InternalLinkSuggestion) => {
      if (!link.url) {
        toast.error('No URL is available for this internal link');
        return;
      }
      if (!tiptapEditor) {
        toast.error('Open the editor to remove internal links');
        return;
      }

      const applied = unlinkFirstExactText(tiptapEditor, link.anchor, link.url);
      if (!applied) {
        toast.error('Could not find that linked anchor text in the document');
        return;
      }

      setInternalLinkSuggestions((current) =>
        current.filter((item) => item.id !== link.id),
      );
    },
    [tiptapEditor],
  );

  const handleRemoveExternalLink = useCallback(
    (link: ExternalLinkItem) => {
      if (!link.url) {
        toast.error('No URL is available for this external link');
        return;
      }
      if (!tiptapEditor) {
        toast.error('Open the editor to remove external links');
        return;
      }

      const applied = unlinkFirstExactText(tiptapEditor, link.anchor, link.url);
      if (!applied) {
        toast.error('Could not find that linked anchor text in the document');
        return;
      }

      setExternalLinkItems((current) => current.filter((item) => item.id !== link.id));
    },
    [tiptapEditor],
  );

  // --- Image alt/caption edits are BODY edits: they update the TipTap image
  //     node attrs directly and never enter metadataPatch. The doc-update
  //     subscription above re-derives docSummary.images so the field reflects
  //     the change. ---
  const handleImageFieldChange = useCallback(
    (id: string, field: 'alt' | 'caption', value: string) => {
      if (!tiptapEditor) {
        toast.error('Open the editor to edit image details');
        return;
      }
      const applied = updateImageNodeField(tiptapEditor, id, field, value);
      if (!applied) {
        toast.error('Could not find that image in the document');
      }
    },
    [tiptapEditor],
  );

  // --- Decorative-image toggle (frontend-only; task #4). Stored in the image
  //     node attrs as `decorative: boolean`. NEVER a backend field — the
  //     metadataPatch shape is unchanged. When the editor isn't mounted the
  //     toggle is disabled in the card. ---
  const handleImageDecorativeToggle = useCallback(
    (id: string, decorative: boolean) => {
      if (!tiptapEditor) {
        toast.error('Open the editor to mark images decorative');
        return;
      }
      const applied = updateImageNodeDecorative(tiptapEditor, id, decorative);
      if (!applied) {
        toast.error('Could not find that image in the document');
      }
    },
    [tiptapEditor],
  );

  // --- Explicit hero designation. Stored in the image node attrs as
  //     `isHero: boolean` (exclusive — clearing every other image node in the
  //     same transaction). Drives the Hero card preview and the Generate AI
  //     replacement target, so "which image is the hero" is never ambiguous. ---
  const handleSetHeroImage = useCallback(
    (id: string) => {
      if (!tiptapEditor) {
        toast.error('Open the editor to choose the hero image');
        return;
      }
      const applied = setHeroImageNode(tiptapEditor, id);
      if (!applied) {
        toast.error('Could not find that image in the document');
        return;
      }
      toast.success('Hero image updated');
    },
    [tiptapEditor],
  );

  // --- Explicit OG image choice. A METADATA edit (og_image_url override via
  //     the existing debounced patch path), not a body edit. Clicking the
  //     current OG image clears the override so publishing falls back to the
  //     featured image. ---
  const handleSetOgImage = useCallback(
    (id: string) => {
      const image = docSummary.images.find((img) => img.id === id);
      if (!image?.src) return;
      if (metadata.ogImage === image.src) {
        updateMetadata({ ogImage: '' });
        toast.info('OG image cleared — sharing falls back to the featured image');
        return;
      }
      updateMetadata({ ogImage: image.src });
      toast.success('OG image set for social sharing');
    },
    [docSummary.images, metadata.ogImage, updateMetadata],
  );

  const handleJumpToImage = useCallback(
    (id: string) => {
      if (!tiptapEditor) return;
      jumpToImageNode(tiptapEditor, id);
    },
    [tiptapEditor],
  );

  const handleJumpToTakeaways = useCallback(() => {
    if (!tiptapEditor) return;
    jumpToTakeawaysNode(tiptapEditor);
  }, [tiptapEditor]);

  const handleAnalyzeImages = useCallback(async () => {
    setImageAnalyzing(true);
    try {
      const response = await editorAIService.getImageOpportunities(
        contentId,
        getEditorSnapshot?.(),
        {
          focus_keyword: focusKeyword,
          title: content?.title ?? '',
        },
      );
      setImageSuggestions(mapImageOpportunities(response.result.opportunities ?? []));
      setImageAnalyzed(true);
    } catch (err) {
      console.error('[rail] image opportunity analysis failed', err);
      toast.error('Could not analyze image opportunities');
    } finally {
      setImageAnalyzing(false);
    }
  }, [content?.title, contentId, focusKeyword, getEditorSnapshot]);

  const handleGenerateOpportunityImage = useCallback(
    async (suggestion: ImageSuggestion) => {
      if (!tenantId) {
        toast.error('Select a tenant before generating images');
        return;
      }
      if (!tiptapEditor) {
        toast.error('Open the editor to insert generated images');
        return;
      }

      const opportunity = (
        imageSuggestions.find((item) => item.id === suggestion.id) ?? suggestion
      ) as RailImageSuggestion;
      const prompt = opportunity.prompt || opportunity.alt;
      const keyword = focusKeyword || content?.keyword || opportunity.alt;

      try {
        const generated = await editorAIService.generateHeroImage(
          {
            title: prompt || content?.title || 'Article image',
            keyword,
            style: 'professional',
          },
          tenantId,
        );

        if (!generated.image_url) {
          toast.error('Image generation finished without an image URL');
          return;
        }

        const inserted = insertImageAtSelection(tiptapEditor, {
          src: generated.image_url,
          alt: generated.alt_text || opportunity.alt,
          title: opportunity.section,
          isAIGenerated: true,
        });

        if (!inserted) {
          toast.error('Generated image, but could not insert it into the editor');
          return;
        }

        const mediaId =
          generated.featured_image_id ??
          generated.media_id ??
          generated.wordpress_media_id;
        if (typeof mediaId === 'number' && Number.isFinite(mediaId)) {
          editorAIService
            .updateFeaturedImageId(contentId, mediaId, tenantId)
            .catch((err) => {
              console.error('[rail] update featured image id failed', err);
            });
        }

        toast.success('Generated and inserted image');
      } catch (err) {
        console.error('[rail] generate opportunity image failed', err);
        toast.error('Could not generate image for this opportunity');
      }
    },
    [
      content?.keyword,
      content?.title,
      contentId,
      focusKeyword,
      imageSuggestions,
      tenantId,
      tiptapEditor,
    ],
  );

  const handleGenerateHeroImage = useCallback(async () => {
    if (!tenantId) {
      toast.error('Select a tenant before generating images');
      return;
    }
    if (!tiptapEditor) {
      toast.error('Open the editor to insert generated images');
      return;
    }

    setHeroGenerating(true);
    try {
      const title = content?.title || focusKeyword || 'Article hero image';
      const keyword = focusKeyword || content?.keyword || title;
      const generated = await editorAIService.generateHeroImage(
        {
          title,
          keyword,
          style: 'professional',
          contentId,
        },
        tenantId,
      );

      if (!generated.image_url) {
        toast.error('Image generation finished without an image URL');
        return;
      }

      const altText = generated.alt_text || `${title} hero image`;
      setHeroImageUrl(generated.image_url);
      setHeroImageAlt(altText);

      // Deterministic target: replace the current hero image node in place
      // (designated via "Set as hero", else the first image). Never insert at
      // the text cursor — that landed generated heroes at random spots.
      const inserted = setHeroImageContent(tiptapEditor, {
        src: generated.image_url,
        alt: altText,
        title: 'Hero image',
        isAIGenerated: true,
      });

      if (!inserted) {
        toast.error('Generated hero image, but could not insert it into the editor');
        return;
      }

      const mediaId =
        generated.featured_image_id ??
        generated.media_id ??
        generated.wordpress_media_id;
      if (typeof mediaId === 'number' && Number.isFinite(mediaId)) {
        editorAIService
          .updateFeaturedImageId(contentId, mediaId, tenantId)
          .catch((err) => {
            console.error('[rail] update featured image id failed', err);
            toast.error('Hero image generated, but could not save the featured image');
          });
      }

      toast.success(
        generated.placeholder
          ? 'Hero image saved to draft storage — it will publish to WordPress when you publish.'
          : 'Generated and inserted hero image',
      );
    } catch (err) {
      console.error('[rail] generate hero image failed', err);
      toast.error('Could not generate hero image');
    } finally {
      setHeroGenerating(false);
    }
  }, [
    content?.keyword,
    content?.title,
    contentId,
    focusKeyword,
    tenantId,
    tiptapEditor,
  ]);

  return (
    <>
    <EditorRail
      defaultTab="tools"
      tools={
        <ToolsTab
          suggestions={{
            suggestions: railSuggestions,
            enabled: suggestionsOn,
            onToggle: setSuggestionsOn,
            onApply: handleApplySuggestion,
            onDismiss: handleDismissSuggestion,
            onGenerate: handleGenerateSuggestions,
            generating: suggestionsGenerating,
          }}
          stats={{
            wordCount,
            headings: headingCount,
            focusTerms: content?.target_keywords?.length ?? 0,
            status: humanizeStatus(content?.status),
          }}
          quality={quality}
          trust={{ signals: trustSignals }}
          coverage={coverage}
          structure={{
            items: structureItems.items,
            onAddSuggested: structureItems.onAddSuggested,
          }}
          hero={{
            // Single source of truth with the Images card: the image the doc
            // summary marked as hero (explicit designation → generated hero →
            // first image). Local generation state remains a fallback for the
            // brief window before the editor re-derives the summary.
            imageUrl:
              railHeroImage?.src ?? heroImageUrl ?? existingHeroImage?.src ?? null,
            imageAlt:
              railHeroImage?.alt ?? heroImageAlt ?? existingHeroImage?.alt ?? null,
            featuredImageId: railContent?.featured_image_id ?? null,
            onGenerate: handleGenerateHeroImage,
            busy: heroGenerating,
          }}
          imageMetadata={{
            images: docSummary.images,
            ogImageSrc: metadata.ogImage || null,
            onImageFieldChange: handleImageFieldChange,
            onImageDecorativeToggle: handleImageDecorativeToggle,
            onJumpToImage: handleJumpToImage,
            onSetHero: handleSetHeroImage,
            onSetOgImage: handleSetOgImage,
            contentId: contentId ?? undefined,
            tenantId,
            metadataGenerating,
          }}
          serp={{
            data: serp,
            onChange: handleSerpChange,
            // Session B adds the optional `fieldStates` prop to SerpPreviewCard;
            // the card looks up meta_title / meta_description / url_slug.
            fieldStates: metadataSummary.fieldStates,
            contentId: contentId ?? undefined,
            tenantId,
            metadataGenerating,
          }}
          metadataRunBanner={
            undoAvailable || revertableFields.length > 0 ? (
              <MetadataRunBanner
                undoAvailable={undoAvailable}
                onUndoRun={handleUndoRun}
                revertableFields={revertableFields}
                onRevertField={handleRevertField}
              />
            ) : null
          }
          summary={{
            excerpt: metadata.excerpt,
            // Publish reality: an empty excerpt falls back to the meta
            // description (route publish maps meta_description → excerpt). The
            // doc's first paragraph is only used when there's no meta description.
            derivedExcerpt: metadata.metaDescription || docSummary.derivedExcerpt,
            onExcerptChange: (excerpt) => updateMetadata({ excerpt }),
            takeaways: docSummary.takeaways,
            onJumpToTakeaways: docSummary.takeaways.length
              ? handleJumpToTakeaways
              : undefined,
            // Session B adds the optional `fieldStates` prop to SummaryCard;
            // the card looks up `excerpt`.
            fieldStates: metadataSummary.fieldStates,
            contentId: contentId ?? undefined,
            tenantId,
            metadataGenerating,
          }}
          structuredData={{
            // Row taps pass their schema type as the drawer focus (scoped
            // open); "Edit details" passes none.
            onInspect: (focus?: SchemaFocusType) => {
              setSchemaDrawerFocus(focus ?? null);
              setSchemaDrawerOpen(true);
            },
            enrichmentPresent: railContent?.postgen_jsonld != null,
            // T4 design ruling: fixed 5-row per-type value presence (see the
            // schemaTypePresence useMemo above) — same lazy schema-preview
            // cache, no new fetch. Empty until the drawer has been opened at
            // least once.
            typePresence: schemaTypePresence,
            // Labeled authoring skeletons on the two actionable rows.
            // metaRunRequesting is included so feedback is INSTANT on click,
            // not deferred to the polled run status.
            authoring:
              schemaRunStatus === 'queued' ||
              schemaRunStatus === 'running' ||
              metaRunRequesting,
          }}
          socialMetadata={{
            ogTitle: metadata.ogTitle,
            ogDescription: metadata.ogDescription,
            twitterTitle: metadata.twitterTitle,
            twitterDescription: metadata.twitterDescription,
            metaTitle: metadata.metaTitle,
            metaDescription: metadata.metaDescription,
            onOgTitleChange: (ogTitle) => updateMetadata({ ogTitle }),
            onOgDescriptionChange: (ogDescription) => updateMetadata({ ogDescription }),
            onTwitterTitleChange: (twitterTitle) => updateMetadata({ twitterTitle }),
            onTwitterDescriptionChange: (twitterDescription) => updateMetadata({ twitterDescription }),
            // Session B adds the optional `fieldStates` prop to SocialMetadataCard;
            // the card looks up og_title / og_description / twitter_title / twitter_description.
            fieldStates: metadataSummary.fieldStates,
            contentId: contentId ?? undefined,
            tenantId,
            metadataGenerating,
          }}
          publishingSettings={{
            canonicalUrl: metadata.canonicalUrl,
            robotsOverride: metadata.robotsOverride,
            wordpressAuthorId: metadata.wordpressAuthorId,
            siteDomain: tenantId,
            slug: metadata.slug,
            authors: wpAuthors.length > 0 ? wpAuthors : undefined,
            onCanonicalUrlChange: (canonicalUrl) => updateMetadata({ canonicalUrl }),
            onRobotsOverrideChange: (robotsOverride) => updateMetadata({ robotsOverride }),
            onWordpressAuthorIdChange: (wordpressAuthorId) => updateMetadata({ wordpressAuthorId }),
            onSlugChange: (slug) => updateMetadata({ slug }),
            keyword: metadata.keyword,
            onKeywordChange: (keyword) => updateMetadata({ keyword }),
            slugAvailability,
            // Family F per-field "Suggest" button (preview mode — URL stakes).
            contentId: contentId ?? undefined,
            tenantId,
            metadataGenerating,
          }}
          internalLinks={{
            links: internalLinkSuggestions,
            onRemove: handleRemoveInternalLink,
          }}
          externalLinks={{
            links: externalLinkItems,
            onRemove: handleRemoveExternalLink,
          }}
          wordPress={wordPress}
          social={{}}
          focusKeyword={{
            keyword: focusKeyword || null,
          }}
          trailing={
            <>
              {content?.wordpress_post_id &&
                content?.generation_metadata?.qa_noindex === true &&
                !indexingAllowed && (
                  <div className="rounded-lg border border-coral/40 bg-coral-light p-4">
                    <p className="text-sm font-medium">Search indexing on hold</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      This post is published with a QA noindex — search engines
                      won't index it until you approve.
                    </p>
                    <button
                      type="button"
                      disabled={indexingSubmitting}
                      onClick={handleAllowIndexing}
                      className="mt-2 rounded-md bg-coral px-3 py-1.5 text-xs font-medium text-white hover:bg-coral-hover disabled:opacity-50"
                    >
                      {indexingSubmitting ? 'Allowing…' : 'Allow indexing'}
                    </button>
                  </div>
                )}
              {trailingTools}
            </>
          }
        />
      }
      comments={
        <CommentsTab
          comments={railComments}
          onReply={handleReply}
          onResolve={handleResolve}
        />
      }
      badges={{
        comments: railComments.filter((c) => !c.resolved).length,
      }}
    />
      <StructuredDataDrawer
        open={schemaDrawerOpen}
        onOpenChange={(nextOpen) => {
          setSchemaDrawerOpen(nextOpen);
          // The row focus is a per-open-session scoped-open input — cleared
          // when the drawer closes, never persisted.
          if (!nextOpen) setSchemaDrawerFocus(null);
        }}
        focusType={schemaDrawerFocus}
        contentId={contentId ?? null}
        tenantId={tenantId}
        enrichment={railContent?.postgen_jsonld ?? null}
        onGenerateSchema={handleGenerateMetadata}
        generating={metaRunRequesting}
        authoredSchema={authoredSchema}
        onEditField={handleEditAuthoredField}
        onForceHowTo={handleForceHowTo}
        schemaRunStatus={schemaRunStatus}
        // Postel prefill source for the empty article-summary socket —
        // offered only as an explicit "Use meta description" click.
        fallbackDescription={metadata.metaDescription || metadata.excerpt || ''}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * T4 rail-card presence row helper: first ~38 chars of an authored value,
 * wrapped in typographic quotes with a trailing ellipsis — the Article row's
 * "value" detail.
 */
function quotedSnippet(text: string, maxLen = 38): string {
  const snippet = text.length > maxLen ? text.slice(0, maxLen) : text;
  return `“${snippet}…”`;
}

/**
 * T4 rail-card presence row helper: builds the "Home › Blog › <post>" trail
 * from the persisted authored breadcrumb particles (already position-sorted
 * by normalizeBreadcrumbs). Returns null when absent so the caller can fall
 * back to the live schema-preview derivation.
 */
/**
 * FAQ presence at the publish gate: publish ships FAQPage ONLY when at
 * least 2 Q&A pairs exist (backend gate). A single pair is therefore a
 * won't-ship state — every presence surface must render it inert and
 * informational, never as a shipping value. 0 pairs stays the normal empty
 * state; the '—' unavailable state is the caller's (no preview cached yet).
 */
export function faqPresenceState(
  questionCount: number,
): Pick<SchemaTypePresence, 'state' | 'detail'> {
  if (questionCount >= 2) {
    return { state: 'value', detail: `${questionCount} questions` };
  }
  if (questionCount === 1) {
    return {
      state: 'insufficient',
      detail: '1 question found — needs at least 2 to ship',
    };
  }
  return { state: 'empty', detail: 'none detected' };
}

export function breadcrumbTrailFromParticles(
  particles: BreadcrumbParticle[] | null | undefined,
): string | null {
  if (!particles || particles.length === 0) return null;
  // Sort defensively — normalizeBreadcrumbs already sorts by position, but
  // this helper is also exported/testable standalone, so don't assume order.
  const names = [...particles]
    .sort((a, b) => a.position - b.position)
    .map((p) => p.name)
    .filter((n) => n.length > 0);
  return names.length > 0 ? names.join(' › ') : null;
}

/**
 * T4 rail-card presence row helper: same trail, derived from the composed
 * schema-preview jsonld graph's BreadcrumbList node (fallback when the
 * authored packet hasn't persisted breadcrumbs yet). Mirrors the accessor in
 * StructuredDataDrawer.tsx's own breadcrumbTrail derivation.
 *
 * Reads the schema.org-standard NESTED ListItem shape emitted by
 * SchemaBuilder.build_breadcrumb_schema (backend/.../schema_builder.py):
 * `itemListElement[].item.name` / `.item["@id"]`, sorted by `position` — NOT
 * a flat `itemListElement[].name` (that shape doesn't exist on the wire; a
 * prior version of this helper read it and always yielded an empty trail,
 * which was invisible because authoredSchema.breadcrumbs — added later —
 * covers the case where POSTGEN_SCHEMA_METADATA_ENABLED is on; this preview
 * fallback is the ACTIVE path everywhere else, including prod today).
 */
export function breadcrumbTrailFromPreview(
  jsonld: Array<Record<string, unknown>> | null | undefined,
): string | null {
  if (!jsonld) return null;
  for (const node of jsonld) {
    if (node['@type'] !== 'BreadcrumbList') continue;
    const items = node.itemListElement;
    if (!Array.isArray(items) || items.length === 0) return null;
    const crumbs = items
      .map((it) => {
        if (!it || typeof it !== 'object') return null;
        const li = it as { position?: unknown; item?: { name?: unknown } };
        const name = li.item && typeof li.item === 'object' ? li.item.name : undefined;
        if (typeof name !== 'string' || name.length === 0) return null;
        const position = typeof li.position === 'number' ? li.position : Number.MAX_SAFE_INTEGER;
        return { name, position };
      })
      .filter((c): c is { name: string; position: number } => c !== null);
    if (crumbs.length === 0) return null;
    crumbs.sort((a, b) => a.position - b.position);
    return crumbs.map((c) => c.name).join(' › ');
  }
  return null;
}

/**
 * T4 rail-card presence row helper: counts FAQPage `mainEntity` items in the
 * composed schema-preview jsonld graph (read-only, body-derived — same
 * source the drawer renders). Sums across every FAQPage node in case the
 * publisher ever emits more than one.
 */
function countFaqQuestions(
  jsonld: Array<Record<string, unknown>> | null | undefined,
): number {
  if (!jsonld) return 0;
  let count = 0;
  for (const node of jsonld) {
    const type = node['@type'];
    const isFaqPage =
      type === 'FAQPage' || (Array.isArray(type) && type.includes('FAQPage'));
    if (!isFaqPage) continue;
    const mainEntity = node.mainEntity;
    if (Array.isArray(mainEntity)) count += mainEntity.length;
  }
  return count;
}

/**
 * postgen field name (metadataPatch.ts set) → MetadataValues key. Used by the
 * per-field revert-to-baseline path. Non-string fields (wordpress_author_id is
 * numeric, schema_types_injected is an array) are omitted because the postgen
 * baseline is typed as string|null and reverting those fields is out of scope
 * for the minimal revert affordance.
 */
const POSTGEN_FIELD_TO_METADATA_KEY: Record<string, keyof MetadataValues | null> = {
  meta_title: 'metaTitle',
  meta_description: 'metaDescription',
  url_slug: 'slug',
  excerpt: 'excerpt',
  og_title: 'ogTitle',
  og_description: 'ogDescription',
  twitter_title: 'twitterTitle',
  twitter_description: 'twitterDescription',
  canonical_url: 'canonicalUrl',
  robots_override: 'robotsOverride',
  wordpress_author_id: null,
  schema_types_injected: null,
};

interface MetadataRunBannerProps {
  undoAvailable: boolean;
  onUndoRun: () => void;
  revertableFields: MetadataFieldState[];
  onRevertField: (field: string, baseline: string) => void;
}

/**
 * Small banner rendered between the metadata control and the SERP card. Shows
 * the whole-run Undo affordance (when a snapshot is available) and a per-field
 * revert-to-baseline list (when the postgen evidence carries baselines).
 * Honest copy — no fake confidence numbers, no model names.
 *
 * Exported for direct unit tests; the mount renders it via the
 * `metadataRunBanner` slot on ToolsTab.
 */
export function MetadataRunBanner({
  undoAvailable,
  onUndoRun,
  revertableFields,
  onRevertField,
}: MetadataRunBannerProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {undoAvailable && (
        <div className="rounded-lg border border-coral/30 bg-coral-light px-3 py-2 flex items-center justify-between gap-2">
          <div className="text-[11.5px] leading-snug">
            <span className="font-medium text-coral-dark">Metadata run applied.</span>{' '}
            <span className="text-muted-foreground">
              Not right? Restore the values from before this run.
            </span>
          </div>
          <button
            type="button"
            onClick={onUndoRun}
            className="shrink-0 px-2 py-0.5 text-[11.5px] font-medium rounded-md border border-coral/40 bg-card text-coral-dark hover:bg-coral hover:text-white"
          >
            Undo this run
          </button>
        </div>
      )}
      {revertableFields.length > 0 && (
        <details className="rounded-lg border border-border bg-card px-3 py-2">
          <summary className="text-[11.5px] font-medium text-muted-foreground cursor-pointer">
            Revert fields to pre-run baseline ({revertableFields.length})
          </summary>
          <div className="mt-2 flex flex-col gap-1">
            {revertableFields.map((fs) => (
              <button
                key={fs.field}
                type="button"
                onClick={() => onRevertField(fs.field, fs.baseline as string)}
                className="text-left text-[11px] px-2 py-1 rounded-md border border-border hover:bg-warm-bg"
              >
                <span className="font-mono">{fs.field}</span>{' '}
                <span className="text-muted-foreground">→ restore baseline</span>
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

interface TextRange {
  from: number;
  to: number;
}

interface ImageAttrs {
  src: string;
  alt?: string;
  title?: string;
  isAIGenerated?: boolean;
}

function replaceFirstExactText(
  editor: Editor,
  needle: string,
  replacement: string,
): boolean {
  const range = findFirstExactTextRange(editor, needle);
  if (!range) return false;

  editor.view.dispatch(editor.state.tr.insertText(replacement, range.from, range.to));
  editor.view.focus();
  return true;
}

function insertImageAtSelection(editor: Editor, attrs: ImageAttrs): boolean {
  return editor.chain().focus().insertContent({ type: 'image', attrs }).run();
}

function unlinkFirstExactText(editor: Editor, anchor: string, url?: string): boolean {
  const link = editor.state.schema.marks.link;
  if (!link) return false;

  const range =
    findFirstExactLinkedTextRange(editor, anchor, url) ??
    findFirstExactLinkedTextRange(editor, anchor);
  if (!range) return false;

  editor.view.dispatch(editor.state.tr.removeMark(range.from, range.to, link));
  editor.view.focus();
  return true;
}

function findFirstExactLinkedTextRange(
  editor: Editor,
  needle: string,
  url?: string,
): TextRange | null {
  if (!needle) return null;

  const link = editor.state.schema.marks.link;
  if (!link) return null;

  let found: TextRange | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found) return false;
    if (!node.isText || !node.text) return true;

    const hasMatchingLink = node.marks.some(
      (mark) => mark.type === link && (url == null || mark.attrs.href === url),
    );
    if (!hasMatchingLink) return true;

    const start = node.text.indexOf(needle);
    if (start < 0) return true;

    found = { from: pos + start, to: pos + start + needle.length };
    return false;
  });

  return found;
}

function findFirstExactTextRange(editor: Editor, needle: string): TextRange | null {
  if (!needle) return null;

  let text = '';
  const positions: number[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    for (let i = 0; i < node.text.length; i += 1) {
      text += node.text[i];
      positions.push(pos + i);
    }
  });

  const start = text.indexOf(needle);
  if (start < 0) return null;

  const from = positions[start];
  const last = positions[start + needle.length - 1];
  if (from == null || last == null) return null;

  return { from, to: last + 1 };
}

/**
 * Schema-type ids the server forces into the publish payload (T3:
 * bridge/core.py::_compute_schema_payload |= {"Article","BreadcrumbList"}).
 * The UI mirrors this so a chip "off" toggle on these types is a no-op —
 * persisting "off" would lie about what ships. Kept as the lowercase id forms
 * the rail uses (DEMO_SCHEMAS ids + label-derived ids for BreadcrumbList).
 */
export const MANDATORY_SCHEMA_IDS: ReadonlySet<string> = new Set([
  'article',
  'breadcrumb',
  'breadcrumblist',
]);

/**
 * Apply a schema-type toggle defensively. Returns the next schemaTypes list,
 * or `null` when the toggle targets a server-mandatory type (Article /
 * BreadcrumbList) — the caller treats `null` as a no-op (T3 defense-in-depth
 * guard in handleToggleSchema). For every other id the result is the touched
 * list (preserving T1 semantics: only the explicitly-toggled chip changes,
 * never the seeded defaults for untouched types).
 */
export function applySchemaToggle(
  current: SchemaType[],
  id: string,
  applied: boolean,
): SchemaType[] | null {
  if (MANDATORY_SCHEMA_IDS.has(id.trim().toLowerCase())) return null;
  return current.map((schema) =>
    schema.id === id ? { ...schema, applied } : schema,
  );
}

export function seedSchemaTypes(appliedTypes: string[] | null | undefined): SchemaType[] {
  if (!Array.isArray(appliedTypes)) {
    // No stored selection (schema_types_injected is null) → seed every type
    // UNCHECKED. Never fall back to the DEMO_SCHEMAS defaults here: those
    // seeded-on chips read as if the user had chosen them, and a single chip
    // toggle then persisted the whole demo array (["Article","HowTo",
    // "BreadcrumbList"]) as if it were user intent — silently dropping
    // FAQPage from the server-side auto-default set. Null must stay "no
    // explicit selection" until the user actually touches a chip.
    // (Article/BreadcrumbList become server-side mandatory in T3.)
    return DEMO_SCHEMAS.map((schema) => ({ ...schema, applied: false }));
  }

  const applied = new Set(appliedTypes.map(normalizeSchemaName).filter(Boolean));
  const seen = new Set<string>();
  const base = DEMO_SCHEMAS.map((schema) => {
    const labelKey = normalizeSchemaName(schema.label);
    const idKey = normalizeSchemaName(schema.id);
    seen.add(labelKey);
    seen.add(idKey);
    return {
      ...schema,
      applied: applied.has(labelKey) || applied.has(idKey),
    };
  });

  const extras = appliedTypes
    .filter((label) => {
      const key = normalizeSchemaName(label);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((label) => ({
      id: schemaId(label),
      label,
      applied: true,
    }));

  return [...base, ...extras];
}

export function schemaLabels(types: SchemaType[]): string[] {
  return types.filter((schema) => schema.applied).map((schema) => schema.label);
}

function schemaId(label: string): string {
  const id = normalizeSchemaName(label).replace(/[^a-z0-9]+/g, '-');
  return id || 'schema';
}

function normalizeSchemaName(label: string): string {
  return label.trim().toLowerCase();
}

function mapImageOpportunities(
  opportunities: EditorImageOpportunity[],
): RailImageSuggestion[] {
  return opportunities.map((opportunity, index) => {
    const imageType = opportunity.image_type ?? 'section';
    const placement = opportunity.placement || 'Suggested placement';
    const alt = opportunity.alt_text || opportunity.prompt || 'Generated article image';
    return {
      id: String(opportunity.id ?? `image-opportunity-${index + 1}`),
      section: opportunity.section || placement || imageType,
      alt,
      placement,
      prompt: opportunity.prompt || alt,
      imageType,
      rationale: opportunity.rationale,
    };
  });
}

function extractFirstContentImage(contentJson: unknown): ExistingContentImage | null {
  if (!contentJson || typeof contentJson !== 'object') return null;

  const stack: unknown[] = [contentJson];
  while (stack.length > 0) {
    const node = stack.shift();
    if (!node || typeof node !== 'object') continue;

    const record = node as Record<string, unknown>;
    if (record.type === 'image' && record.attrs && typeof record.attrs === 'object') {
      const attrs = record.attrs as Record<string, unknown>;
      if (typeof attrs.src === 'string' && attrs.src.trim()) {
        return {
          src: attrs.src,
          alt: typeof attrs.alt === 'string' ? attrs.alt : null,
        };
      }
    }

    if (Array.isArray(record.content)) {
      stack.push(...record.content);
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Document-derived summary (Summary / Images / Structured-data cards)
// ---------------------------------------------------------------------------

interface JsonNode {
  type?: string;
  attrs?: Record<string, unknown>;
  text?: string;
  content?: JsonNode[];
}

/** Stable id for an image: its blockId when present, else its document ordinal. */
function imageNodeId(attrs: Record<string, unknown>, ordinal: number): string {
  const blockId =
    typeof attrs.blockId === 'string' && attrs.blockId.trim() ? attrs.blockId.trim() : '';
  return blockId || `img-${ordinal}`;
}

function isKeyTakeawaysCallout(attrs: Record<string, unknown>): boolean {
  return attrs.variant === 'key-takeaways' || attrs.type === 'key-takeaways';
}

/** Build an EditorImageMeta from raw image attrs (hero is assigned post-walk). */
function toImageMeta(
  attrs: Record<string, unknown>,
  ordinal: number,
  src: string,
): EditorImageMeta {
  return {
    id: imageNodeId(attrs, ordinal),
    src,
    alt: typeof attrs.alt === 'string' ? attrs.alt : '',
    caption:
      typeof attrs.caption === 'string' && attrs.caption ? attrs.caption : undefined,
    decorative: attrs.decorative === true ? true : undefined,
  };
}

/**
 * Mark exactly one image as the hero. Precedence: an image node explicitly
 * designated via the `isHero` attr (rail "Set as hero" button), else the image
 * matching the known hero src (freshly generated hero / first persisted
 * image), else the document's first image.
 */
function assignHeroFlag(
  images: EditorImageMeta[],
  explicitHeroIds: Set<string>,
  heroSrc: string | null,
): void {
  if (images.length === 0) return;
  const hero =
    images.find((img) => explicitHeroIds.has(img.id)) ??
    (heroSrc ? images.find((img) => img.src === heroSrc) : undefined) ??
    images[0];
  hero.isHero = true;
}

/** Derive the summary from the LIVE TipTap doc (authoritative once the editor mounts). */
function summarizeEditorDoc(editor: Editor, heroSrc: string | null): EditorDocSummary {
  const images: EditorImageMeta[] = [];
  const takeaways: string[] = [];
  let derivedExcerpt = '';
  let hasFaqBlocks = false;
  let imageOrdinal = 0;
  const explicitHeroIds = new Set<string>();

  // First top-level paragraph → derived excerpt.
  editor.state.doc.forEach((node) => {
    if (derivedExcerpt || node.type.name !== 'paragraph') return;
    const text = node.textContent.trim();
    if (text) derivedExcerpt = text;
  });

  editor.state.doc.descendants((node) => {
    const name = node.type.name;
    if (name === 'faqSchema' || name === 'faqItem') hasFaqBlocks = true;

    if (name === 'image') {
      const attrs = node.attrs as Record<string, unknown>;
      const src = typeof attrs.src === 'string' ? attrs.src : '';
      const ordinal = imageOrdinal;
      imageOrdinal += 1;
      if (src) {
        const meta = toImageMeta(attrs, ordinal, src);
        if (attrs.isHero === true) explicitHeroIds.add(meta.id);
        images.push(meta);
      }
      return false;
    }

    if (name === 'callout' && isKeyTakeawaysCallout(node.attrs as Record<string, unknown>)) {
      let foundList = false;
      node.descendants((child) => {
        if (child.type.name !== 'listItem') return true;
        foundList = true;
        const text = child.textContent.trim();
        if (text) takeaways.push(text);
        return false;
      });
      if (!foundList) {
        node.descendants((child) => {
          if (child.type.name !== 'paragraph') return true;
          const text = child.textContent.trim();
          if (text) takeaways.push(text);
          return false;
        });
      }
      return false;
    }

    return true;
  });

  assignHeroFlag(images, explicitHeroIds, heroSrc);
  return { derivedExcerpt, takeaways, images, hasFaqBlocks };
}

/** Concatenate all text within a content_json node subtree. */
function jsonNodeText(node: JsonNode | undefined): string {
  if (!node) return '';
  if (typeof node.text === 'string') return node.text;
  return (node.content ?? []).map(jsonNodeText).join('');
}

function collectJsonTakeaways(callout: JsonNode): string[] {
  const items: string[] = [];
  const listItems: JsonNode[] = [];
  const findListItems = (node: JsonNode | undefined): void => {
    if (!node) return;
    if (node.type === 'listItem') {
      listItems.push(node);
      return;
    }
    (node.content ?? []).forEach(findListItems);
  };
  findListItems(callout);

  if (listItems.length) {
    for (const li of listItems) {
      const text = jsonNodeText(li).trim();
      if (text) items.push(text);
    }
    return items;
  }

  // Fallback: paragraphs (the callout title is a heading, so it's skipped).
  const findParagraphs = (node: JsonNode | undefined): void => {
    if (!node) return;
    if (node.type === 'paragraph') {
      const text = jsonNodeText(node).trim();
      if (text) items.push(text);
      return;
    }
    (node.content ?? []).forEach(findParagraphs);
  };
  findParagraphs(callout);
  return items;
}

/** Fallback: derive the summary from persisted content_json before the editor mounts. */
function summarizeContentJson(
  contentJson: unknown,
  heroSrc: string | null,
): EditorDocSummary {
  if (!contentJson || typeof contentJson !== 'object') return EMPTY_DOC_SUMMARY;
  const root = contentJson as JsonNode;

  const images: EditorImageMeta[] = [];
  const takeaways: string[] = [];
  let derivedExcerpt = '';
  let hasFaqBlocks = false;
  let imageOrdinal = 0;
  const explicitHeroIds = new Set<string>();

  for (const child of root.content ?? []) {
    if (child?.type === 'paragraph') {
      const text = jsonNodeText(child).trim();
      if (text) {
        derivedExcerpt = text;
        break;
      }
    }
  }

  const visit = (node: JsonNode | undefined): void => {
    if (!node || typeof node !== 'object') return;
    const type = node.type;
    if (type === 'faqSchema' || type === 'faqItem') hasFaqBlocks = true;

    if (type === 'image') {
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const src = typeof attrs.src === 'string' ? attrs.src : '';
      const ordinal = imageOrdinal;
      imageOrdinal += 1;
      if (src) {
        const meta = toImageMeta(attrs, ordinal, src);
        if (attrs.isHero === true) explicitHeroIds.add(meta.id);
        images.push(meta);
      }
      return;
    }

    if (type === 'callout' && isKeyTakeawaysCallout((node.attrs ?? {}) as Record<string, unknown>)) {
      takeaways.push(...collectJsonTakeaways(node));
      return;
    }

    (node.content ?? []).forEach(visit);
  };
  visit(root);

  assignHeroFlag(images, explicitHeroIds, heroSrc);
  return { derivedExcerpt, takeaways, images, hasFaqBlocks };
}

/** Update an image node's alt/caption attr in place. No editor focus (keeps the rail input focused). */
function updateImageNodeField(
  editor: Editor,
  id: string,
  field: 'alt' | 'caption',
  value: string,
): boolean {
  let targetPos: number | null = null;
  let targetAttrs: Record<string, unknown> | null = null;
  let ordinal = 0;
  editor.state.doc.descendants((node, pos) => {
    if (targetPos != null || node.type.name !== 'image') {
      return targetPos == null;
    }
    const attrs = node.attrs as Record<string, unknown>;
    if (imageNodeId(attrs, ordinal) === id) {
      targetPos = pos;
      targetAttrs = attrs;
    }
    ordinal += 1;
    return false;
  });

  if (targetPos == null || targetAttrs == null) return false;
  const nextAttrs = { ...(targetAttrs as Record<string, unknown>), [field]: value };
  editor.view.dispatch(editor.state.tr.setNodeMarkup(targetPos, undefined, nextAttrs));
  return true;
}

/**
 * Set the per-image `decorative` node attr (task #4). The flag lives in the
 * image node attrs only — NEVER a backend metadata field. The doc-update
 * subscription in the mount re-derives docSummary.images so the chip reflects
 * the change immediately. When `decorative` is set true and alt is non-empty,
 * the alt is left untouched (the user can clear it from the rail input).
 */
function updateImageNodeDecorative(
  editor: Editor,
  id: string,
  decorative: boolean,
): boolean {
  let targetPos: number | null = null;
  let targetAttrs: Record<string, unknown> | null = null;
  let ordinal = 0;
  editor.state.doc.descendants((node, pos) => {
    if (targetPos != null || node.type.name !== 'image') {
      return targetPos == null;
    }
    const attrs = node.attrs as Record<string, unknown>;
    if (imageNodeId(attrs, ordinal) === id) {
      targetPos = pos;
      targetAttrs = attrs;
    }
    ordinal += 1;
    return false;
  });

  if (targetPos == null || targetAttrs == null) return false;
  const nextAttrs = { ...(targetAttrs as Record<string, unknown>), decorative };
  editor.view.dispatch(editor.state.tr.setNodeMarkup(targetPos, undefined, nextAttrs));
  return true;
}

/**
 * Exclusively mark one image node as the hero: sets `isHero` on the matching
 * node and clears it from every other image node in a single transaction, so
 * the designation can never be duplicated or ambiguous.
 */
function setHeroImageNode(editor: Editor, id: string): boolean {
  const imageNodes: Array<{
    pos: number;
    attrs: Record<string, unknown>;
    isTarget: boolean;
  }> = [];
  let found = false;
  let ordinal = 0;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'image') return true;
    const attrs = node.attrs as Record<string, unknown>;
    const isTarget = imageNodeId(attrs, ordinal) === id;
    if (isTarget) found = true;
    imageNodes.push({ pos, attrs, isTarget });
    ordinal += 1;
    return false;
  });
  if (!found) return false;

  const tr = editor.state.tr;
  for (const { pos, attrs, isTarget } of imageNodes) {
    if (Boolean(attrs.isHero) === isTarget) continue;
    tr.setNodeMarkup(pos, undefined, { ...attrs, isHero: isTarget });
  }
  if (tr.docChanged) editor.view.dispatch(tr);
  return true;
}

/**
 * Put a freshly generated hero image into the doc deterministically: replace
 * the current hero image node in place when one exists (explicit designation,
 * else the first image), otherwise insert at the very top of the document.
 * Stale dimensions are cleared so the new image renders at its natural aspect
 * ratio; the node's blockId is retained so its rail identity stays stable.
 */
function setHeroImageContent(editor: Editor, attrs: ImageAttrs): boolean {
  let heroPos: number | null = null;
  let heroAttrs: Record<string, unknown> | null = null;
  let firstPos: number | null = null;
  let firstAttrs: Record<string, unknown> | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'image') return heroPos == null;
    const nodeAttrs = node.attrs as Record<string, unknown>;
    if (firstPos == null) {
      firstPos = pos;
      firstAttrs = nodeAttrs;
    }
    if (heroPos == null && nodeAttrs.isHero === true) {
      heroPos = pos;
      heroAttrs = nodeAttrs;
    }
    return false;
  });

  // Casts needed: TS can't see the assignments inside the descendants callback,
  // so it narrows these to their `null` initializers (same as the other helpers).
  const targetPos = (heroPos ?? firstPos) as number | null;
  const targetAttrs = (heroAttrs ?? firstAttrs) as Record<string, unknown> | null;
  if (targetPos != null && targetAttrs != null) {
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(targetPos, undefined, {
        ...targetAttrs,
        ...attrs,
        isHero: true,
        width: null,
        height: null,
        aspectRatio: null,
      }),
    );
    return true;
  }

  return editor
    .chain()
    .focus()
    .insertContentAt(0, { type: 'image', attrs: { ...attrs, isHero: true } })
    .run();
}

/** Scroll the editor to an image node and select it. */
function jumpToImageNode(editor: Editor, id: string): void {
  let targetPos: number | null = null;
  let ordinal = 0;
  editor.state.doc.descendants((node, pos) => {
    if (targetPos != null || node.type.name !== 'image') {
      return targetPos == null;
    }
    if (imageNodeId(node.attrs as Record<string, unknown>, ordinal) === id) {
      targetPos = pos;
    }
    ordinal += 1;
    return false;
  });
  if (targetPos == null) return;
  editor.chain().focus().setNodeSelection(targetPos).scrollIntoView().run();
}

/** Scroll the editor to the key-takeaways callout. */
function jumpToTakeawaysNode(editor: Editor): void {
  let targetPos: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (targetPos != null) return false;
    if (
      node.type.name === 'callout' &&
      isKeyTakeawaysCallout(node.attrs as Record<string, unknown>)
    ) {
      targetPos = pos;
      return false;
    }
    return true;
  });
  if (targetPos == null) return;
  editor.chain().focus().setTextSelection(targetPos + 1).scrollIntoView().run();
}

function countOccurrences(haystack: string, needle: string): number {
  if (!haystack || !needle) return 0;
  const re = new RegExp(`\\b${escapeRegExp(needle)}\\b`, 'gi');
  return haystack.match(re)?.length ?? 0;
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getSelectedText(editor: Editor | null | undefined): string {
  if (!editor) return '';
  const { from, to } = editor.state.selection;
  return from !== to ? editor.state.doc.textBetween(from, to, ' ') : '';
}

function humanizeStatus(status?: string | null): string {
  if (!status) return 'Draft';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Authored-schema helpers (LOCKED contract — Session C wiring).
// ---------------------------------------------------------------------------

/**
 * Read the authored-schema fields packet off RailContentDetail defensively.
 *
 * Source order:
 *   1. `content.postgen_schema_fields` — the typed extractor Session A adds to
 *      ContentDetail (mirror of extract_postgen_jsonld).
 *   2. `content.generation_metadata.postgen_schema.fields` — the same packet
 *      reachable on the wire today inside the JSONB blob. Tolerates partial /
 *      malformed shapes (returns null on any guard failure).
 *
 * Never throws — null means "no authored schema on this row yet".
 */
export function readAuthoredSchema(
  content: RailContentDetail | undefined,
): AuthoredSchemaFields | null {
  if (!content) return null;

  // Source 1: typed extractor (Session A).
  const direct = content.postgen_schema_fields;
  if (direct && typeof direct === 'object' && typeof direct.article_description === 'string') {
    return normalizeAuthoredSchema(direct);
  }

  // Source 2: walk the JSONB blob.
  const gm = content.generation_metadata as
    | { postgen_schema?: { fields?: unknown } }
    | undefined;
  const fieldsRaw = gm?.postgen_schema?.fields;
  if (!fieldsRaw || typeof fieldsRaw !== 'object') return null;
  return normalizeAuthoredSchema(fieldsRaw);
}

/** Coerce an unknown packet into AuthoredSchemaFields or null (defensive). */
export function normalizeAuthoredSchema(raw: unknown): AuthoredSchemaFields | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const article_description =
    typeof r.article_description === 'string' ? r.article_description : '';
  const is_howto = r.is_howto === true;
  const howto = isHowToObject(r.howto) ? (r.howto as HowToObject) : null;
  const breadcrumbs = normalizeBreadcrumbs(r.breadcrumbs);
  const faq = normalizeFaq(r.faq);
  return { article_description, is_howto, howto, breadcrumbs, faq };
}

/** Coerce an unknown value into a BreadcrumbParticle[] or null (defensive). */
function normalizeBreadcrumbs(raw: unknown): BreadcrumbParticle[] | null {
  if (!Array.isArray(raw)) return null;
  // url is OPTIONAL — the backend persists name+position only (publish
  // always re-derives the breadcrumb fresh at compose time, so a persisted
  // url has no consumer; the editor trail display is names-only). Requiring
  // it here used to filter out every real persisted crumb, making the
  // primary path dead and silently falling back to the lazy preview.
  const crumbs = raw.filter(
    (c): c is BreadcrumbParticle =>
      !!c &&
      typeof c === 'object' &&
      typeof (c as Record<string, unknown>).name === 'string' &&
      typeof (c as Record<string, unknown>).position === 'number',
  );
  return crumbs.length > 0 ? crumbs.sort((a, b) => a.position - b.position) : null;
}

/** Coerce an unknown value into an AuthoredFaq or null (defensive). */
function normalizeFaq(raw: unknown): AuthoredFaq | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const has_faq = r.has_faq === true;
  const pairsRaw = Array.isArray(r.pairs) ? r.pairs : [];
  const pairs = pairsRaw.filter(
    (p): p is { question: string; answer: string } =>
      !!p &&
      typeof p === 'object' &&
      typeof (p as Record<string, unknown>).question === 'string' &&
      typeof (p as Record<string, unknown>).answer === 'string',
  );
  return { has_faq, pairs };
}

/** Type guard: a HowTo object has a name + >=2 steps each with name+text. */
function isHowToObject(v: unknown): v is HowToObject {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  if (typeof o.name !== 'string') return false;
  if (!Array.isArray(o.steps)) return false;
  if (o.steps.length < 2) return false;
  return o.steps.every(
    (s) =>
      s && typeof s === 'object' &&
      typeof (s as Record<string, unknown>).name === 'string' &&
      typeof (s as Record<string, unknown>).text === 'string',
  );
}

/** Structural equality for two HowTo objects (or null). */
function howToEqual(a: HowToObject | null, b: HowToObject | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.name !== b.name) return false;
  if (a.steps.length !== b.steps.length) return false;
  return a.steps.every((s, i) => {
    const other = b.steps[i];
    return !!other && s.name === other.name && s.text === other.text;
  });
}

/**
 * Walk the TipTap document for link marks and split them into internal
 * (relative URLs, in-page anchors, or this tenant's domain) vs external.
 * This is the article's REAL link inventory — the source of truth the
 * internal/external link cards render.
 */
function extractLinksFromDoc(
  doc: unknown,
  tenantId: string,
): { internal: InternalLinkSuggestion[]; external: InternalLinkSuggestion[] } {
  const internal: InternalLinkSuggestion[] = [];
  const external: InternalLinkSuggestion[] = [];
  if (!doc || typeof doc !== 'object') return { internal, external };
  const seen = new Set<string>();

  const visit = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    const n = node as { text?: string; marks?: Array<{ type?: string; attrs?: { href?: string } }>; content?: unknown[] };
    const href = n.marks?.find((m) => m?.type === 'link')?.attrs?.href;
    if (href && n.text) {
      const key = `${href}|${n.text}`;
      if (!seen.has(key)) {
        seen.add(key);
        const isInternal =
          href.startsWith('/') ||
          href.startsWith('#') ||
          (tenantId ? href.includes(tenantId) : false);
        const list = isInternal ? internal : external;
        list.push({
          id: `${isInternal ? 'il' : 'el'}-doc-${seen.size}`,
          anchor: n.text,
          target: href,
          url: href,
        });
      }
    }
    if (Array.isArray(n.content)) n.content.forEach(visit);
  };
  visit(doc);
  return { internal, external };
}

/**
 * Map real quality signals → ContentQualityCardProps. Returns null when no
 * real score exists — the rail then renders the "Not analyzed yet" empty
 * state. Sub-sections render ONLY for signals we actually have; NEVER
 * reintroduce placeholder scores or a demo-data fallback here: fabricated
 * scores on real articles misled QA into reporting a "quality score" on
 * empty content.
 *
 * Score precedence: quality-report overall → content_score (judge column) →
 * validation_results.content_score.overall → quality_scores.pipeline_score.
 */
/**
 * Map the quality report's Reality / Authority / Temporal validator sections
 * into TrustSignal rows for the rail. A signal is emitted ONLY when its
 * section actually ran (`enabled`) — a not-run section is omitted so the card
 * shows its "Trust analysis has not run" empty state rather than a fabricated
 * result. NEVER fall back to demo data here (same rule as qualityToCardProps).
 */
function trustToSignals(report: unknown): TrustSignal[] {
  const r = (report ?? {}) as Partial<QualityReportResponse>;
  const signals: TrustSignal[] = [];

  const reality = r.reality_filter;
  if (reality?.enabled) {
    const total = reality.statistics_found?.length ?? 0;
    const verified = reality.verified_statistics?.length ?? 0;
    signals.push({
      id: 'reality',
      label: 'Reality filter',
      status:
        reality.issue_count > 0
          ? `${reality.issue_count} unverified claim${reality.issue_count === 1 ? '' : 's'}`
          : 'All claims verified',
      value: total > 0 ? `${verified}/${total}` : String(Math.round(reality.score)),
      tone: reality.passed ? 'success' : reality.score < 50 ? 'danger' : 'warning',
    });
  }

  const authority = r.authority_signals;
  if (authority?.enabled) {
    const broken = authority.broken_count ?? 0;
    signals.push({
      id: 'authority',
      label: 'Authority signals',
      status:
        broken > 0
          ? `${broken} broken source${broken === 1 ? '' : 's'}`
          : authority.threshold_met
            ? 'Authoritative sources cited'
            : 'Needs more authoritative sources',
      value: String(authority.count),
      tone: authority.threshold_met ? (broken > 0 ? 'warning' : 'success') : 'warning',
    });
  }

  const temporal = r.temporal_relevancy;
  if (temporal?.enabled) {
    signals.push({
      id: 'temporal',
      label: 'Temporal relevancy',
      status:
        temporal.issue_count > 0
          ? `${temporal.issue_count} outdated reference${temporal.issue_count === 1 ? '' : 's'}`
          : 'Up to date',
      value: String(temporal.current_year),
      tone: temporal.passed ? 'success' : 'warning',
    });
  }

  return signals;
}

function qualityToCardProps(
  report: unknown,
  content: ContentDetail | undefined,
): ContentQualityCardProps | null {
  const r = (report ?? {}) as Record<string, unknown>;
  const vScore = content?.validation_results?.content_score;
  const qs = content?.quality_scores;

  const overall =
    (typeof r.overall_score === 'number' ? r.overall_score : null) ??
    (typeof content?.content_score === 'number' ? content.content_score : null) ??
    vScore?.overall ??
    (typeof qs?.pipeline_score === 'number' ? qs.pipeline_score : null);

  if (!overall) return null;

  // Pills: only real sub-signals (generation pipeline writing + anchor/link
  // quality). No hardcoded 80/75/85 defaults.
  const pills: { label: string; score: number }[] = [];
  if (typeof qs?.writing_score === 'number') {
    pills.push({ label: 'Writing', score: Math.round(qs.writing_score) });
  }
  if (typeof qs?.anchor_score === 'number') {
    pills.push({ label: 'Links', score: Math.round(qs.anchor_score) });
  }

  // SEO section ← anchor-quality report (keyword/anchor issues are SEO findings).
  const seo =
    typeof qs?.anchor_score === 'number'
      ? {
          score: Math.round(qs.anchor_score),
          findings: (qs.anchor_issues ?? [])
            .filter((i) => i?.detail)
            .map((i) => ({
              text: i.anchor ? `"${i.anchor}" — ${i.detail}` : String(i.detail),
              warning: i.severity === 'error' || i.severity === 'warning',
            })),
        }
      : null;

  // Structure section ← per-section writing scores from the generation pipeline.
  const structure =
    typeof qs?.writing_score === 'number'
      ? {
          score: Math.round(qs.writing_score),
          findings: (qs.section_summaries ?? [])
            .filter((s) => s?.summary)
            .map((s) => ({
              text: String(s.summary),
              warning: typeof s.score === 'number' && s.score < 60,
            })),
        }
      : null;

  // Judge section ← the overall judge score (the content_score column).
  const judge =
    typeof content?.content_score === 'number'
      ? {
          score: Math.round(content.content_score),
          summary: 'Overall grade from the content-quality judge for this article.',
        }
      : null;

  return {
    overallScore: Math.round(overall),
    grade: gradeFromScore(overall),
    delta: null,
    pills: pills.length ? pills : undefined,
    seo,
    // Authority signals (citation/trust validators) aren't surfaced yet —
    // omitted rather than faked.
    authority: null,
    structure,
    judge,
  };
}

function gradeFromScore(score: number): string {
  if (score >= 95) return 'A+';
  if (score >= 90) return 'A';
  if (score >= 85) return 'B+';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

/** Flatten the block-grouped comment map into a flat list for CommentsList. */
function flattenComments(
  byBlock: CommentsWithRepliesByBlock | undefined,
): RailComment[] {
  if (!byBlock) return [];
  const out: RailComment[] = [];
  for (const blockComments of Object.values(byBlock)) {
    for (const c of blockComments) {
      out.push({
        id: c.id,
        authorName: c.author_name,
        body: c.comment_text,
        createdAt: c.created_at,
        resolved: c.status === 'resolved',
        quote: c.text_snippet ?? null,
        replies: c.replies?.map(toReply),
      });
    }
  }
  // Open first, then resolved; within each newest first.
  out.sort((a, b) => {
    if (Boolean(a.resolved) !== Boolean(b.resolved)) return a.resolved ? 1 : -1;
    return b.createdAt.localeCompare(a.createdAt);
  });
  return out;
}

function toReply(c: ContentComment) {
  return {
    id: c.id,
    authorName: c.author_name,
    body: c.comment_text,
    createdAt: c.created_at,
  };
}
